/**
 * Offsets that keep sticky and scrolled-to content clear of the site's sticky
 * chrome (c-ov-09, 2026-10-07). Measured at every width from 360 to 1440px:
 *  - the site header (LayoutHeader) is `sticky top-0 z-40` and 100px tall;
 *  - from sm, PanchangContextStrip sticks directly under it (z-30), 34px plus a
 *    1px border, so the sticky chrome ends at 135px there;
 *  - below md, the bottom nav bar and the floating chat pill are fixed to the
 *    bottom of the screen.
 *
 * One owner, so a header change moves every offset together. Before this, the
 * 100px lived in four literals and the result header stuck at 100px from md,
 * exactly where the strip sits, so the strip swallowed every "New Kundali"
 * click. src/components/sticky-under-header.test.tsx ties these values to the
 * strip height and fails if any other source file spells them, and
 * tests/e2e/mobile-overlay-overlap.spec.ts checks them on the live page at
 * 360px and 1440px.
 *
 * Keep these as whole literal class strings: Tailwind finds classes by scanning
 * source text, so a class assembled at runtime would never be generated.
 */

/** `top` for a sticky band directly under the site header (100px). */
export const BELOW_SITE_HEADER_TOP = "top-25";

/**
 * `scroll-margin-top` for an element scrolled to the top of the viewport: a
 * result, the form it replaces, and its loading skeleton. 112px on phones
 * (12px under the header) and 148px from sm (13px under header + strip).
 */
export const BELOW_STICKY_CHROME_SCROLL_MARGIN = "scroll-mt-28 sm:scroll-mt-37";

/**
 * BELOW_STICKY_CHROME_SCROLL_MARGIN in px, for code that measures a box
 * (getBoundingClientRect) instead of styling it: phones, and from sm.
 * @internal
 */
export const BELOW_STICKY_CHROME_CLEARANCE_PX = { base: 112, sm: 148 } as const;

/** The clearance at the current width: the strip is `hidden sm:block` (40rem). */
export function belowStickyChromeClearancePx(): number {
  const fromSm = window.matchMedia?.("(min-width: 40rem)").matches ?? false;
  return fromSm ? BELOW_STICKY_CHROME_CLEARANCE_PX.sm : BELOW_STICKY_CHROME_CLEARANCE_PX.base;
}

/**
 * Clearance under the /chat consultation screen's own slim sticky header
 * (ChatScreenHeader: one text row plus py-3, about 48px), with the same 12px
 * gap as the site header's clearance. /chat renders bare, without the site
 * header, the panchang strip or the bottom chrome (ChromeGate).
 */
export const CHAT_SCREEN_HEADER_CLEARANCE_PX = 60;

/**
 * Height the fixed bottom chrome (the bottom nav bar plus the floating chat
 * pill) covers on a phone: content whose top starts lower than
 * `innerHeight - FLOATING_BOTTOM_CHROME_PX` is not really on screen.
 */
export const FLOATING_BOTTOM_CHROME_PX = 130;
